/**
 * Bliss Patios: Request a Quote dialog, WhatsApp links and inquiry analytics.
 *
 * Markup comes from snippets/bliss-inquiry.liquid (dialog, form templates, config)
 * and snippets/bliss-inquiry-buttons.liquid (triggers + product JSON). Any element
 * with [data-bq-open] opens the dialog for the product described by the nearest
 * script[data-bq-product]. Submissions go to the inquiry backend; success is only
 * shown after the backend confirms the inquiry was stored.
 */
(() => {
  'use strict';

  if (window.BlissInquiry) return;

  const configEl = document.getElementById('BlissInquiryConfig');
  const dialog = document.getElementById('BlissInquiryDialog');
  if (!configEl || !dialog) return;

  const config = JSON.parse(configEl.textContent);

  // Translations arrive HTML-escaped from the Liquid t filter.
  const decoder = document.createElement('textarea');
  const decode = (value) => {
    decoder.innerHTML = value || '';
    return decoder.value;
  };
  const S = {};
  Object.keys(config.strings).forEach((key) => {
    S[key] = decode(config.strings[key]);
  });
  const fill = (template, token, value) => template.replace(token, () => value);

  const FORM_TYPES = ['furniture', 'pool', 'general'];
  const USES = ['residence', 'commercial', 'hospitality', 'other'];
  const TIMELINES = ['within_1_month', '1_3_months', '3_plus_months'];
  const LIMITS = { name: 120, city: 120, location: 200, quantity: 60, details: 2000 };
  const SUBMIT_TIMEOUT_MS = 20000;
  const ATTRIBUTION_KEY = 'bq_attribution';
  const ATTRIBUTION_TTL_MS = 90 * 24 * 60 * 60 * 1000;

  const newId = () =>
    window.crypto && crypto.randomUUID
      ? crypto.randomUUID()
      : Array.from({ length: 4 }, () => Math.random().toString(16).slice(2, 10)).join('-');

  /* ---------------------------------------------------------------- analytics */

  // GA4 is loaded by the "AB : Google Analytics" app embed, which keeps gtag
  // private. Commands pushed onto the same dataLayer after its config command
  // share the page_view client ID. Product views are already sent by that app
  // (view_item) and by Shopify Analytics, so they are not duplicated here.
  function gtag() {
    window.dataLayer.push(arguments);
  }

  const analytics = {
    lastClick: new Map(),

    allowed() {
      const privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (privacy && typeof privacy.analyticsProcessingAllowed === 'function') {
        return privacy.analyticsProcessingAllowed();
      }
      return true;
    },

    whenGtagConfigured(callback, attempt = 0) {
      const layer = window.dataLayer;
      if (Array.isArray(layer) && layer.some((entry) => entry && entry[0] === 'config')) {
        callback();
      } else if (attempt < 60) {
        setTimeout(() => this.whenGtagConfigured(callback, attempt + 1), 250);
      }
    },

    params(ctx, extra) {
      const params = {
        item_id: String(ctx.productId),
        item_name: ctx.title,
        form_type: ctx.formType,
        entry_point: ctx.entry,
        language: config.locale,
      };
      if (ctx.sku) params.sku = ctx.sku;
      if (ctx.variantTitle) params.item_variant = ctx.variantTitle;
      return Object.assign(params, extra);
    },

    // No personal data: product identifiers, form type and entry point only.
    send(gaEvent, shopifyEvent, ctx, extra) {
      const params = this.params(ctx, extra);
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        try {
          window.Shopify.analytics.publish(shopifyEvent, params);
        } catch (error) {
          /* customer events are best-effort */
        }
      }
      if (!this.allowed()) return;
      this.whenGtagConfigured(() => gtag('event', gaEvent, params));
    },

    // Ignore double clicks on the same control so one intent is one event.
    click(gaEvent, shopifyEvent, ctx) {
      const key = `${gaEvent}:${ctx.productId}:${ctx.entry}`;
      const now = Date.now();
      if (now - (this.lastClick.get(key) || 0) < 1000) return;
      this.lastClick.set(key, now);
      this.send(gaEvent, shopifyEvent, ctx);
    },

    // A lead is counted once per stored inquiry, even if the success view is re-shown.
    lead(ctx, reference) {
      const storeKey = 'bq_leads';
      let sent = [];
      try {
        sent = JSON.parse(sessionStorage.getItem(storeKey) || '[]');
      } catch (error) {
        sent = [];
      }
      if (sent.includes(reference)) return;
      sent.push(reference);
      try {
        sessionStorage.setItem(storeKey, JSON.stringify(sent.slice(-20)));
      } catch (error) {
        /* storage unavailable: still send once for this page */
      }
      this.send('generate_lead', 'bliss_inquiry_submitted', ctx, { lead_source: 'quote_form', inquiry_reference: reference });
    },
  };

  // First-touch traffic source (UTM / click ID / referrer host), stored with the
  // inquiry so the team can see where leads come from. No personal data.
  const attribution = {
    capture() {
      if (!analytics.allowed()) return;
      try {
        const query = new URLSearchParams(window.location.search);
        const utm = {};
        ['source', 'medium', 'campaign', 'term', 'content'].forEach((key) => {
          const value = query.get(`utm_${key}`);
          if (value) utm[key] = value.slice(0, 100);
        });
        const clickId = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid'].find((key) => query.get(key)) || '';
        let referrer = '';
        if (document.referrer) {
          const host = new URL(document.referrer).hostname;
          if (host !== window.location.hostname) referrer = host;
        }
        const stored = this.get();
        const hasCampaign = Object.keys(utm).length > 0 || clickId;
        if (hasCampaign || !stored) {
          const record = Object.assign(utm, { click_id: clickId, referrer, landing: window.location.pathname, ts: Date.now() });
          localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(record));
        }
      } catch (error) {
        /* storage or URL parsing unavailable */
      }
    },
    get() {
      try {
        const record = JSON.parse(localStorage.getItem(ATTRIBUTION_KEY) || 'null');
        return record && Date.now() - record.ts < ATTRIBUTION_TTL_MS ? record : null;
      } catch (error) {
        return null;
      }
    },
  };

  /* ---------------------------------------------------------- product context */

  function readProduct(trigger) {
    const scope = trigger.closest('[data-bq-scope]') || trigger;
    const json = trigger.querySelector('script[data-bq-product]') || scope.querySelector('script[data-bq-product]');
    if (!json) return null;
    try {
      return JSON.parse(json.textContent);
    } catch (error) {
      return null;
    }
  }

  // The theme re-renders variant-selects [data-selected-variant] on every option
  // change (main product and quick view), so it is the source of truth.
  function selectedVariant(product, trigger) {
    const variants = product.variants || [];
    const byId = (id) => variants.find((variant) => String(variant.id) === String(id));
    const info = trigger.closest('product-info');
    let id = null;
    const selected = info && info.querySelector('variant-selects [data-selected-variant]');
    if (selected) {
      try {
        const variant = JSON.parse(selected.textContent);
        if (variant && variant.id) id = variant.id;
      } catch (error) {
        id = null;
      }
    }
    if (!id && info && info.dataset.updateUrl === 'true') {
      id = new URLSearchParams(window.location.search).get('variant');
    }
    return byId(id) || byId(product.variantId) || variants[0] || null;
  }

  function buildContext(product, variant, entry) {
    const showVariant = Boolean(product.hasVariants && variant);
    return {
      productId: product.id,
      handle: product.handle,
      title: product.title,
      formType: FORM_TYPES.includes(product.formType) ? product.formType : 'general',
      variantId: variant ? variant.id : product.variantId,
      variantTitle: showVariant ? variant.title : '',
      sku: (variant && variant.sku) || '',
      image: (variant && variant.image) || product.image || '',
      url: showVariant ? `${product.url}?variant=${variant.id}` : product.url,
      entry: entry || 'product_page',
    };
  }

  function contextFor(trigger) {
    const product = readProduct(trigger);
    if (!product) return null;
    return buildContext(product, selectedVariant(product, trigger), trigger.dataset.bqEntry);
  }

  /* ----------------------------------------------------------------- WhatsApp */

  function whatsappHref(ctx, reference) {
    if (!config.whatsapp) return '';
    const lines = [S.wa_greeting, fill(S.wa_product, '[value]', ctx.title)];
    if (ctx.sku) lines.push(fill(S.wa_sku, '[value]', ctx.sku));
    if (ctx.variantTitle) lines.push(fill(S.wa_option, '[value]', ctx.variantTitle));
    lines.push(fill(S.wa_link, '[value]', ctx.url));
    if (ctx.image) lines.push(fill(S.wa_image, '[value]', ctx.image));
    if (reference) lines.push(fill(S.wa_reference, '[value]', reference));
    return `https://wa.me/${config.whatsapp}?text=${encodeURIComponent(lines.join('\n'))}`;
  }

  function refreshWhatsApp(link) {
    const ctx = contextFor(link);
    if (ctx) link.href = whatsappHref(ctx);
    return ctx;
  }

  const refreshAllWhatsApp = () => document.querySelectorAll('[data-bq-whatsapp]').forEach(refreshWhatsApp);

  /* ------------------------------------------------------------- validation */

  const toAsciiDigits = (value) =>
    value
      .replace(/[٠-٩]/g, (digit) => String(digit.charCodeAt(0) - 0x0660))
      .replace(/[۰-۹]/g, (digit) => String(digit.charCodeAt(0) - 0x06f0));

  // Returns E.164 (+201012345678) or '' if invalid. Numbers without a country
  // code are treated as Egyptian (01x / 1x / 2x landline).
  function normalizePhone(raw) {
    let value = toAsciiDigits(String(raw || '')).trim().replace(/[\s\-().\/‎‏‪-‮]/g, '');
    if (value.startsWith('00')) value = `+${value.slice(2)}`;
    if (!value.startsWith('+')) {
      if (!/^\d+$/.test(value)) return '';
      if (value.startsWith('20') && value.length === 12) value = value.slice(2);
      value = value.replace(/^0/, '');
      if (value.length < 9 || value.length > 10) return '';
      value = `+20${value}`;
    }
    if (!/^\+[1-9]\d{7,14}$/.test(value)) return '';
    if (value.startsWith('+20') && (value.length < 12 || value.length > 13)) return '';
    return value;
  }

  const EMAIL = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

  function collect(form) {
    const get = (name) => (form.elements[name] ? String(form.elements[name].value || '').trim() : '');
    return {
      name: get('name'),
      phone: get('phone'),
      email: get('email'),
      city: get('city'),
      location: get('location'),
      intended_use: get('intended_use'),
      quantity: get('quantity'),
      timeline: get('timeline'),
      details: get('details'),
      company_website: form.elements.company_website ? form.elements.company_website.value : '',
    };
  }

  function validate(values, form) {
    const errors = {};
    if (values.name.length < 2) errors.name = S.error_name;
    else if (values.name.length > LIMITS.name) errors.name = S.error_too_long;
    if (!normalizePhone(values.phone)) errors.phone = S.error_phone;
    if (!EMAIL.test(values.email) || values.email.length > 254) errors.email = S.error_email;
    if (values.city.length < 2) errors.city = S.error_city;
    else if (values.city.length > LIMITS.city) errors.city = S.error_too_long;
    ['location', 'quantity', 'details'].forEach((name) => {
      if (form.elements[name] && values[name].length > LIMITS[name]) errors[name] = S.error_too_long;
    });
    return errors;
  }

  /* ------------------------------------------------------------------ dialog */

  const titleEl = dialog.querySelector('#BlissInquiryTitle');
  const successTitle = dialog.querySelector('#BlissInquirySuccessTitle');
  const formView = dialog.querySelector('[data-bq-view="form"]');
  const successView = dialog.querySelector('[data-bq-view="success"]');
  const slot = dialog.querySelector('[data-bq-form-slot]');
  const live = dialog.querySelector('[data-bq-live]');
  const card = {
    img: dialog.querySelector('[data-bq-product-img]'),
    title: dialog.querySelector('[data-bq-product-title]'),
    sku: dialog.querySelector('[data-bq-product-sku]'),
    variant: dialog.querySelector('[data-bq-product-variant]'),
    intro: dialog.querySelector('[data-bq-intro]'),
  };

  const forms = {};
  let ctx = null;
  let lastTrigger = null;

  const announce = (message) => {
    live.textContent = '';
    setTimeout(() => {
      live.textContent = message;
    }, 50);
  };

  // Each form type is cloned once and kept, so values survive closing the
  // dialog or a failed submission. It is only reset after a confirmed success.
  function formState(type) {
    if (forms[type]) return forms[type];
    const template = document.getElementById(`BlissInquiryForm-${type}`);
    const form = template.content.querySelector('form').cloneNode(true);
    const state = { type, form, submissionId: newId(), productKey: '', startedAt: 0, busy: false, widgetId: null, token: '' };
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      submit(state);
    });
    form.querySelector('[data-bq-retry]').addEventListener('click', () => submit(state));
    form.addEventListener('input', (event) => clearFieldError(event.target));
    forms[type] = state;
    return state;
  }

  function renderProductCard(context) {
    card.title.textContent = context.title;
    if (context.image) {
      card.img.src = context.image.replace(/(\?|&)width=\d+/, '$1width=240');
      card.img.alt = context.title;
      card.img.hidden = false;
    } else {
      card.img.hidden = true;
    }
    card.sku.hidden = !context.sku;
    card.sku.replaceChildren();
    if (context.sku) {
      const sku = document.createElement('bdi');
      sku.dir = 'ltr';
      sku.textContent = context.sku;
      card.sku.append(`${S.sku}: `, sku);
    }
    card.variant.hidden = !context.variantTitle;
    card.variant.textContent = context.variantTitle ? `${S.option}: ${context.variantTitle}` : '';
    card.intro.textContent = S[`intro_${context.formType}`];
  }

  function showView(name) {
    formView.hidden = name !== 'form';
    successView.hidden = name !== 'success';
    dialog.setAttribute('aria-labelledby', name === 'form' ? 'BlissInquiryTitle' : 'BlissInquirySuccessTitle');
    if (name === 'form') dialog.setAttribute('aria-describedby', 'BlissInquiryIntro');
    else dialog.removeAttribute('aria-describedby');
  }

  function open(trigger) {
    const context = contextFor(trigger);
    if (!context) return;
    ctx = context;
    lastTrigger = trigger;

    const state = formState(ctx.formType);
    const productKey = `${ctx.productId}:${ctx.variantId}`;
    if (state.productKey !== productKey) {
      state.productKey = productKey;
      state.submissionId = newId();
    }
    if (!state.startedAt) state.startedAt = Date.now();

    renderProductCard(ctx);
    hideFormError(state.form);
    if (slot.firstElementChild !== state.form) slot.replaceChildren(state.form);
    showView('form');

    if (!dialog.open) dialog.showModal();
    document.documentElement.classList.add('bq-lock');
    dialog.querySelector('[data-bq-panel]').scrollTop = 0;
    titleEl.focus({ preventScroll: true });

    analytics.click('quote_click', 'bliss_quote_click', ctx);
    if (config.turnstileSiteKey) prepareTurnstile(state);
  }

  function close() {
    if (dialog.open) dialog.close();
  }

  dialog.addEventListener('close', () => {
    document.documentElement.classList.remove('bq-lock');
    if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus({ preventScroll: true });
  });

  // Escape closes only this dialog, not a quick view modal underneath it.
  dialog.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') event.stopPropagation();
  });

  dialog.addEventListener('click', (event) => {
    if (event.target === dialog || event.target.closest('[data-bq-close]')) close();
  });

  /* ------------------------------------------------------------- form states */

  function fieldParts(form, name) {
    const input = form.elements[name];
    const field = input && input.closest('[data-bq-field]');
    return { input, field, error: field && field.querySelector('.bq-error') };
  }

  function clearFieldError(input) {
    if (!input || !input.name) return;
    const { field, error } = fieldParts(input.form, input.name);
    if (!error) return;
    input.removeAttribute('aria-invalid');
    field.classList.remove('bq-field--invalid');
    error.hidden = true;
    error.textContent = '';
  }

  function showFieldErrors(form, errors) {
    Array.from(form.elements).forEach((element) => {
      if (element.name && !(element.name in errors)) clearFieldError(element);
    });
    let first = null;
    Object.keys(errors).forEach((name) => {
      const { input, field, error } = fieldParts(form, name);
      if (!input || !error) return;
      input.setAttribute('aria-invalid', 'true');
      field.classList.add('bq-field--invalid');
      error.textContent = errors[name];
      error.hidden = false;
      if (!first) first = input;
    });
    return first;
  }

  function showFormError(form, message, canRetry) {
    const box = form.querySelector('[data-bq-form-error]');
    box.querySelector('[data-bq-form-error-text]').textContent = message;
    box.querySelector('[data-bq-retry]').hidden = !canRetry;
    box.hidden = false;
    box.setAttribute('role', 'alert');
    box.focus({ preventScroll: false });
  }

  function hideFormError(form) {
    const box = form.querySelector('[data-bq-form-error]');
    box.hidden = true;
    box.removeAttribute('role');
  }

  function setBusy(state, busy) {
    state.busy = busy;
    const button = state.form.querySelector('[data-bq-submit]');
    button.disabled = busy;
    button.classList.toggle('bq-submit--busy', busy);
    button.querySelector('[data-bq-submit-label]').textContent = busy ? S.sending : S.submit;
    state.form.setAttribute('aria-busy', String(busy));
    if (busy) announce(S.sending);
  }

  /* ---------------------------------------------------------------- Turnstile */

  let turnstileScript = null;

  function loadTurnstile() {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (!turnstileScript) {
      turnstileScript = new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.onload = () => resolve(window.turnstile || null);
        script.onerror = () => resolve(null);
        document.head.appendChild(script);
      });
    }
    return turnstileScript;
  }

  async function prepareTurnstile(state) {
    const turnstile = await loadTurnstile();
    const holder = state.form.querySelector('[data-bq-turnstile]');
    if (!turnstile || !holder || state.widgetId !== null) return;
    state.widgetId = turnstile.render(holder, {
      sitekey: config.turnstileSiteKey,
      appearance: 'interaction-only',
      language: config.locale,
      callback: (token) => {
        state.token = token;
      },
      'expired-callback': () => {
        state.token = '';
      },
    });
  }

  async function turnstileToken(state) {
    await prepareTurnstile(state);
    for (let i = 0; i < 40 && !state.token; i += 1) {
      await new Promise((resolve) => setTimeout(resolve, 250));
    }
    return state.token;
  }

  function resetTurnstile(state) {
    state.token = '';
    if (window.turnstile && state.widgetId !== null) window.turnstile.reset(state.widgetId);
  }

  /* --------------------------------------------------------------- submission */

  async function submit(state) {
    if (state.busy || !ctx) return;
    const { form } = state;
    const submitCtx = ctx;
    hideFormError(form);

    const values = collect(form);
    const errors = validate(values, form);
    const firstInvalid = showFieldErrors(form, errors);
    if (firstInvalid) {
      announce(S.error_summary);
      firstInvalid.focus();
      return;
    }
    if (!config.endpoint) {
      showFormError(form, S.error_unavailable, false);
      return;
    }

    setBusy(state, true);
    let token = '';
    if (config.turnstileSiteKey) {
      token = await turnstileToken(state);
      if (!token) {
        setBusy(state, false);
        showFormError(form, S.error_verification, true);
        return;
      }
    }

    const payload = {
      submissionId: state.submissionId,
      formType: state.type,
      locale: config.locale,
      fields: {
        name: values.name,
        phone: normalizePhone(values.phone),
        email: values.email,
        city: values.city,
        location: values.location,
        intended_use: USES.includes(values.intended_use) ? values.intended_use : '',
        quantity: values.quantity,
        timeline: TIMELINES.includes(values.timeline) ? values.timeline : '',
        details: values.details,
      },
      product: {
        id: submitCtx.productId,
        variantId: submitCtx.variantId,
        handle: submitCtx.handle,
        title: submitCtx.title,
        variantTitle: submitCtx.variantTitle,
        sku: submitCtx.sku,
        url: submitCtx.url,
        image: submitCtx.image,
      },
      context: {
        entryPoint: submitCtx.entry,
        pageUrl: window.location.origin + window.location.pathname,
        attribution: attribution.get(),
      },
      antiSpam: {
        website: values.company_website,
        elapsedMs: Date.now() - state.startedAt,
        turnstileToken: token,
      },
    };

    let response;
    let data = null;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), SUBMIT_TIMEOUT_MS);
    try {
      response = await fetch(config.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'omit',
        signal: controller.signal,
      });
      data = await response.json().catch(() => null);
    } catch (error) {
      setBusy(state, false);
      showFormError(form, S.error_network, true);
      return;
    } finally {
      clearTimeout(timer);
    }
    setBusy(state, false);
    if (config.turnstileSiteKey) resetTurnstile(state);

    if (response.ok && data && data.ok && data.reference) {
      succeed(state, submitCtx, data.reference);
      return;
    }
    if (response.status === 422 && data && data.errors) {
      const mapped = {};
      Object.keys(data.errors).forEach((name) => {
        mapped[name] = S[`error_${name}`] || S.error_too_long;
      });
      const first = showFieldErrors(form, mapped);
      announce(S.error_summary);
      if (first) first.focus();
      return;
    }
    if (response.status === 429) {
      showFormError(form, S.error_rate, true);
      return;
    }
    if (response.status === 403 && data && data.error === 'verification_failed') {
      showFormError(form, S.error_verification, true);
      return;
    }
    showFormError(form, S.error_server, true);
  }

  function succeed(state, submitCtx, reference) {
    analytics.lead(submitCtx, reference);

    const body = successView.querySelector('[data-bq-success-body]');
    body.replaceChildren();
    if (submitCtx.sku) {
      const [before, after] = S.success_body_sku.split('[sku]');
      const sku = document.createElement('bdi');
      sku.dir = 'ltr';
      sku.textContent = submitCtx.sku;
      body.append(before, sku, after || '');
    } else {
      body.textContent = fill(S.success_body, '[product]', submitCtx.title);
    }
    const ref = successView.querySelector('[data-bq-success-ref]');
    const [refBefore, refAfter] = S.success_reference.split('[reference]');
    const refValue = document.createElement('bdi');
    refValue.dir = 'ltr';
    refValue.textContent = reference;
    ref.replaceChildren(refBefore, refValue, refAfter || '');

    const wa = successView.querySelector('[data-bq-success-wa]');
    const href = whatsappHref(submitCtx, reference);
    wa.hidden = !href;
    if (href) wa.href = href;
    wa.dataset.bqEntry = 'success';

    showView('success');
    successTitle.focus({ preventScroll: true });
    announce(successTitle.textContent);

    state.form.reset();
    Array.from(state.form.elements).forEach(clearFieldError);
    state.submissionId = newId();
    state.startedAt = 0;
    state.productKey = '';
  }

  /* ------------------------------------------------------------------ wiring */

  // Capture phase so legacy popup handlers on the same element never run.
  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target.closest('[data-bq-open]');
      if (trigger) {
        event.preventDefault();
        event.stopPropagation();
        open(trigger);
        return;
      }
      const whatsapp = event.target.closest('[data-bq-whatsapp]');
      if (whatsapp) {
        const context = refreshWhatsApp(whatsapp);
        if (context) analytics.click('whatsapp_click', 'bliss_whatsapp_click', context);
        return;
      }
      const successWhatsapp = event.target.closest('[data-bq-success-wa]');
      if (successWhatsapp && ctx) {
        analytics.click('whatsapp_click', 'bliss_whatsapp_click', Object.assign({}, ctx, { entry: 'success' }));
      }
    },
    true
  );

  // Keep hrefs current for middle-click, long-press and "copy link".
  ['focusin', 'pointerdown'].forEach((type) =>
    document.addEventListener(type, (event) => {
      const link = event.target.closest && event.target.closest('[data-bq-whatsapp]');
      if (link) refreshWhatsApp(link);
    })
  );

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, () => setTimeout(refreshAllWhatsApp, 0));
  }

  attribution.capture();
  refreshAllWhatsApp();

  window.BlissInquiry = { open, close, config, normalizePhone, version: '1.0.0' };
})();
